<template>
  <div class="header-mobile-menu">
    <SButtonIcon
      action-type="secondary"
      :aria-expanded="isOpen"
      :aria-label="tr('main_site.layout.header_open_menu')"
      icon="fa fa-bars"
      @click="toggleMenu"
    />

    <Teleport to="body">
      <Transition name="header-mobile-menu__panel">
        <div v-if="isOpen" class="header-mobile-menu__panel">
          <div class="header-mobile-menu__content">
            <div class="header-mobile-menu__top">
              <SButtonSearch
                always-open
                :aria-label="tr('main_site.layout.header_search_placeholder')"
                fluid
                :placeholder="tr('main_site.layout.header_search_placeholder')"
                @search="onSearch"
              />
              <SButtonIcon
                action-type="tertiary-soft"
                :aria-label="tr('main_site.layout.header_close_menu')"
                icon="fas fa-xmark"
                @click="closeMenu"
              />
            </div>

            <nav
              v-if="config"
              class="header-mobile-menu__nav"
              data-gtm-container="header-menu"
            >
              <PAccordion
                class="header-mobile-menu__accordion"
                collapse-icon="fas fa-chevron-up"
                expand-icon="fas fa-chevron-down"
                multiple
                :value="openPanels"
              >
                <PAccordionPanel
                  v-for="(section, sectionIndex) in config.sections"
                  :key="sectionIndex"
                  :data-gtm-container="`header-menu-${sectionIndex === 0 ? 'b2c' : 'b2b'}`"
                  :value="sectionIndex === 0 ? 'b2c' : 'b2b'"
                >
                  <PAccordionHeader
                    class="header-mobile-menu__accordion-header"
                  >
                    {{
                      sectionIndex === 0
                        ? tr("main_site.layout.header_projects_b2c")
                        : tr("main_site.layout.header_projects_b2b")
                    }}
                  </PAccordionHeader>
                  <PAccordionContent>
                    <ul class="header-mobile-menu__links">
                      <li v-for="link in section.links" :key="link.href">
                        <SButton
                          action-type="quaternary"
                          fluid
                          :href="link.href"
                          :prefix-icon="link.icon"
                          size="md"
                          tag="a"
                          text-align="left"
                        >
                          {{ link.label }}
                        </SButton>
                      </li>
                      <li v-if="section.viewAllLink">
                        <SButton
                          action-type="quaternary"
                          color="primary"
                          fluid
                          :href="section.viewAllLink.href"
                          prefix-icon="fa-light fa-grid-2"
                          size="md"
                          tag="a"
                          text-align="left"
                        >
                          {{ section.viewAllLink.label }}
                        </SButton>
                      </li>
                    </ul>
                  </PAccordionContent>
                </PAccordionPanel>
              </PAccordion>
            </nav>

            <div class="header-mobile-menu__actions">
              <SButton
                action-type="tertiary-soft"
                class="header-mobile-menu__action"
                :href="ziggyUnsafe('client.pro.customer_registration')"
                size="sm"
                tag="a"
              >
                {{ tr("main_site.layout.header_to_customer") }}
              </SButton>
              <SButton
                class="header-mobile-menu__action"
                :href="ziggyUnsafe('auth.login')"
                prefix-icon="far fa-arrow-right"
                size="sm"
                tag="a"
              >
                {{ tr("main_site.layout.header_login") }}
              </SButton>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { useMediaQuery, useScrollLock } from "@vueuse/core";
import {
  AccordionContent as PAccordionContent,
  AccordionHeader as PAccordionHeader,
  AccordionPanel as PAccordionPanel,
} from "primevue";
import { computed, ref, watch } from "vue";

import { SButton, SButtonIcon, SButtonSearch } from "@solvari/common-fe";
import { ziggyUnsafe } from "@solvari/common-fe/integrations";
import { useI18nMainSiteLayout } from "@solvari/translations";

import PAccordion from "@/atoms/PAccordion.vue";

import { useNavigation } from "./useNavigation";

const { tr } = useI18nMainSiteLayout();
const { navigationConfig: config } = useNavigation();

const isOpen = ref(false);
const isDesktop = useMediaQuery("(min-width: 1000px)");
const isBodyLocked = useScrollLock(
  typeof document !== "undefined" ? document.body : null,
);

watch(isOpen, (open) => {
  isBodyLocked.value = open;
});

watch(isDesktop, (desktop) => {
  if (desktop) {
    closeMenu();
  }
});

const openPanels = computed(() => {
  if (!config.value) return [];
  return config.value.sections.length === 2 ? ["b2c", "b2b"] : ["b2c"];
});

function toggleMenu() {
  isOpen.value = !isOpen.value;
}

function closeMenu() {
  isOpen.value = false;
}

function onSearch(term: string) {
  window.location.href = `${ziggyUnsafe("client.search.base")}?term=${encodeURIComponent(term)}`;
}
</script>

<style lang="postcss">
.header-mobile-menu {
  @screen 1000 {
    @apply hidden;
  }

  &__panel {
    @apply fixed inset-0 z-[60] flex flex-col bg-white;

    &-enter-active,
    &-leave-active {
      @apply transition-opacity duration-200;
    }

    &-enter-from,
    &-leave-to {
      @apply opacity-0;
    }
  }

  &__content {
    @apply flex h-full flex-col;
  }

  &__top {
    @apply flex items-center border-b border-b-slate-100 px-2 py-3.5 gap-2;
  }

  &__nav {
    @apply flex-1 overflow-y-auto px-4 py-2;
  }

  &__accordion {
    @apply flex flex-col gap-2;
  }

  &__accordion-header {
    @apply text-lg font-semibold;
  }

  &__links {
    @apply m-0 list-none p-0;
  }

  &__actions {
    @apply flex border-t border-slate-200 p-4 gap-2;
  }

  &__action {
    @apply flex-1 justify-center;
  }
}
</style>
